<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalGlobalDiscount v-if="modalShowGlobalDiscount" @closeModal="closeModal" :globalDiscount="selectedGlobalDiscount" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Global Discount" />
      <div class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton v-if="role_id !== 'null'" to_url="#" button_text="+ Tambah Global Discount" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Nomor Global Discount</TableHeadCell>
                    <TableHeadCell>Amount</TableHeadCell>
                    <TableHeadCell>PIN Manager</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allGlobalDiscounts.length == 0">
                    <TableCell colspan="5" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(globalDiscount, index) in allGlobalDiscounts" :key="globalDiscount.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ globalDiscount.name }}</TableCell>
                    <TableCell>{{ globalDiscount.amount }} %</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-success dark:bg-success text-light-50 dark:text-white': globalDiscount.need_pin,
                        'bg-danger dark:bg-danger text-light-50 dark:text-white': !globalDiscount.need_pin
                      }" class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ globalDiscount.need_pin ? "Ya" : "Tidak" }}
                      </span>
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + globalDiscount.id" @click="editGlobalDiscount(globalDiscount)"
                        class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                      </button>
                      <button :id="'delete-data-' + globalDiscount.id" @click="deleteGlobalDiscount(globalDiscount)"
                        class="bg-danger hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalGlobalDiscount from "@/components/modals/ModalGlobalDiscount.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "TableView",
  components: {
    IndexView, PageTitle, AddButton, ModalGlobalDiscount, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, Toast, ConfirmDialog, Button
  },
  data() {
    return {
      role_id: localStorage.getItem("role_id"),
      modalShowGlobalDiscount: false,
      isBlur: false,
      selectedGlobalDiscount: null,
      currentPage: 1,
      totalPages: 1,
    }
  },
  computed: {
    ...mapGetters('global_discounts', ['allGlobalDiscounts'])
  },
  created() {
    this.fetchGlobalDiscounts();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchGlobalDiscounts({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions('global_discounts', ['deleteGlobalDiscountById']),
    async fetchGlobalDiscounts(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch('global_discounts/fetchGlobalDiscounts', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getStatusText(status) {
      const statusMap = {
        'available': 'Kosong',
        'unavailable': 'Terisi'
      };
      return statusMap[status] || status;
    },
    showModal() {
      this.modalShowGlobalDiscount = true;
      this.isBlur = true;
      this.selectedGlobalDiscount = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowGlobalDiscount = false;
        this.isBlur = false;
        this.fetchGlobalDiscounts();
      }, 200);
    },
    editGlobalDiscount(globalDiscount) {
      this.selectedGlobalDiscount = globalDiscount;
      this.modalShowGlobalDiscount = true;
      this.isBlur = true;
    },
    async deleteGlobalDiscount(globalDiscount) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus diskon global?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteGlobalDiscountById(globalDiscount.id);
            this.fetchGlobalDiscounts();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus diskon global', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus diskon global', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus diskon global', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus diskon global', life: 3000 });
        }
      });
    }
  },
}
</script>
